import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';
import { Opportunity, OpportunityFilters } from '../types';
import { opportunityAPI } from '../services/api';
import { getComparableDueValue } from '../utils/contractFormatting';
import { opportunityMatchesFilters } from '../utils/opportunityFilters';


interface OpportunityContextType {
  opportunities: Opportunity[];
  filteredOpportunities: Opportunity[];
  filters: OpportunityFilters | null;
  premiumPreferences: {
    procuringOrganizations: string[];
    procuringTypes: string[];
    industries: string[];
    keywords: string[];
  };
  loading: boolean;
  error: string | null;
  viewMode: 'cards' | 'table';
  sortField: string;
  sortDirection: 'asc' | 'desc';
  appliedFilters: {
    industries: string[];
    opportunityTypes: string[];
    procuringTypes: string[];
    preBidMeeting: string[];
    procuringOrganizations: string[];
    searchTerm: string;
  };
  loadOpportunities: (mode: 'browse' | 'feed') => Promise<void>;
  loadFilters: () => Promise<void>;
  loadPremiumPreferences: () => Promise<void>;
  savePremiumPreferences: (preferences: { procuringOrganizations: string[]; procuringTypes: string[]; industries: string[]; keywords: string[] }) => Promise<void>;
  setViewMode: (mode: 'cards' | 'table') => void;
  setSortField: (field: string) => void;
  setSortDirection: (direction: 'asc' | 'desc') => void;
  applyFilters: (filters: { industries: string[]; opportunityTypes: string[]; procuringTypes: string[]; preBidMeeting: string[]; procuringOrganizations: string[]; searchTerm: string }) => void;
}

const OpportunityContext = createContext<OpportunityContextType | undefined>(undefined);

export const useOpportunities = () => {
  const context = useContext(OpportunityContext);
  if (context === undefined) {
    throw new Error('useOpportunities must be used within an OpportunityProvider');
  }
  return context;
};

interface OpportunityProviderProps {
  children: ReactNode;
}

export const OpportunityProvider: React.FC<OpportunityProviderProps> = ({ children }) => {
  const STALE_OVERDUE_DAYS = 7;
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [filteredOpportunities, setFilteredOpportunities] = useState<Opportunity[]>([]);
  const [filters, setFilters] = useState<OpportunityFilters | null>(null);
  const [premiumPreferences, setPremiumPreferences] = useState<{
    procuringOrganizations: string[];
    procuringTypes: string[];
    industries: string[];
    keywords: string[];
  }>({ procuringOrganizations: [], procuringTypes: [], industries: [], keywords: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const VIEW_MODE_STORAGE_KEY = 'opportunities:viewMode';
  const [viewMode, setViewModeState] = useState<'cards' | 'table'>(() => {
    if (typeof window !== 'undefined') {
      const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
      if (stored === 'cards' || stored === 'table') {
        return stored;
      }
    }
    return 'table';
  });
  const setViewMode = useCallback((mode: 'cards' | 'table') => {
    setViewModeState(mode);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
      } catch (error) {
        console.error('Unable to persist view mode preference', error);
      }
    }
  }, []);
  const [sortField, setSortField] = useState('due_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [appliedFilters, setAppliedFilters] = useState({
    industries: [] as string[],
    opportunityTypes: [] as string[],
    procuringTypes: [] as string[],
    preBidMeeting: [] as string[],
    procuringOrganizations: [] as string[],
    searchTerm: '',
  });

  // Load filters once for the app (after loadFilters is defined)

  const loadOpportunities = useCallback(async (mode: 'browse' | 'feed' = 'browse') => {
    setLoading(true);
    setError(null);
    try {
      const response = await opportunityAPI.getOpportunities(mode);
      const items = response.data.opportunities ?? response.data.contracts ?? [];
      setOpportunities(items);
      setFilteredOpportunities(items);
    } catch (err) {
      setError('Failed to load opportunities');
      console.error('Error loading opportunities:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFilters = useCallback(async () => {
    try {
      const response = await opportunityAPI.getLiveFilters();
      setFilters({
        industries: response.data.industries ?? [],
        opportunityTypes: response.data.opportunity_types ?? [],
        procuringTypes: response.data.procuring_types ?? [],
        preBidMeeting: [],
        procuringOrganizations: response.data.procuring_organizations ?? response.data.cities ?? [],
      });
    } catch (err) {
      console.error('Error loading filters:', err);
    }
  }, []);

  // Load filters once for the app
  React.useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  const loadPremiumPreferences = useCallback(async () => {
    try {
      const response = await opportunityAPI.getPremiumPreferences();
      const prefs = response.data.preferences ?? { cities: [], procuring_types: [], industries: [], keywords: [] };
      setPremiumPreferences({
        procuringOrganizations: prefs.procuring_organizations ?? prefs.cities ?? [],
        procuringTypes: prefs.procuring_types ?? [],
        industries: prefs.industries ?? [],
        keywords: prefs.keywords ?? [],
      });
    } catch (err) {
      console.error('Error loading premium preferences:', err);
    }
  }, []);

  const savePremiumPreferences = useCallback(async (preferences: { procuringOrganizations: string[]; procuringTypes: string[]; industries: string[]; keywords: string[] }) => {
    try {
      await opportunityAPI.savePremiumPreferences({
        preferences: {
          cities: preferences.procuringOrganizations,
          procuring_types: preferences.procuringTypes,
          industries: preferences.industries,
          keywords: preferences.keywords,
        },
      });
      setPremiumPreferences(preferences);
    } catch (err) {
      throw err;
    }
  }, []);

  const sortOpportunitiesList = useCallback((list: Opportunity[]) => {
    const sorted = [...list];

    sorted.sort((a, b) => {
      const getComparableDate = (value: any) => {
        if (!value || value === 'TBD') return NaN;
        // Parse YYYY-MM-DD as local date to avoid timezone skew in day-based comparisons.
        const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/;
        if (typeof value === 'string' && isoDateOnly.test(value)) {
          const [year, month, day] = value.split('-').map((part) => Number(part));
          const time = new Date(year, month - 1, day).getTime();
          return Number.isNaN(time) ? NaN : time;
        }
        const time = new Date(value).getTime();
        return Number.isNaN(time) ? NaN : time;
      };

      const overdueBucket = (value: any) => {
        const timestamp = getComparableDate(value);
        if (Number.isNaN(timestamp)) return 2; // TBD last
        const dueDate = new Date(timestamp);
        const today = new Date();
        const localToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const msPerDay = 24 * 60 * 60 * 1000;
        const dayDiff = Math.floor((dueDate.getTime() - localToday.getTime()) / msPerDay);
        if (dayDiff >= 0) return 0; // Active/upcoming
        if (Math.abs(dayDiff) <= STALE_OVERDUE_DAYS) return 1; // Recently overdue
        return 2; // Stale overdue
      };

      let aValue: any = a[sortField as keyof Opportunity];
      let bValue: any = b[sortField as keyof Opportunity];

      if (sortField === 'due_date' || sortField === 'release_date') {
        const aComparable = sortField === 'due_date' ? getComparableDueValue(a) : aValue;
        const bComparable = sortField === 'due_date' ? getComparableDueValue(b) : bValue;
        const aDate = getComparableDate(aComparable);
        const bDate = getComparableDate(bComparable);

        if (sortField === 'due_date' && sortDirection === 'asc') {
          const aBucket = overdueBucket(aComparable);
          const bBucket = overdueBucket(bComparable);
          if (aBucket !== bBucket) return aBucket - bBucket;
        }

        // If both NaN, treat as equal
        if (Number.isNaN(aDate) && Number.isNaN(bDate)) return 0;
        // Push TBD (NaN) entries to the end regardless of sort direction
        if (Number.isNaN(aDate)) return 1;
        if (Number.isNaN(bDate)) return -1;

        if (aDate < bDate) return sortDirection === 'asc' ? -1 : 1;
        if (aDate > bDate) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [sortField, sortDirection]);

  const applyFilters = useCallback((filters: { industries: string[]; opportunityTypes: string[]; procuringTypes: string[]; preBidMeeting: string[]; procuringOrganizations: string[]; searchTerm: string }) => {
    setAppliedFilters(filters);
    
    const filtered = opportunities.filter((contract) => opportunityMatchesFilters(contract, filters));

    setFilteredOpportunities(sortOpportunitiesList(filtered));
  }, [opportunities, sortOpportunitiesList]);

  // Re-sort whenever sort settings change
  React.useEffect(() => {
    setFilteredOpportunities((prev) => sortOpportunitiesList(prev));
  }, [sortOpportunitiesList]);

  const value: OpportunityContextType = useMemo(() => ({
    opportunities,
    filteredOpportunities,
    filters,
    premiumPreferences,
    loading,
    error,
    viewMode,
    sortField,
    sortDirection,
    appliedFilters,
    loadOpportunities,
    loadFilters,
    loadPremiumPreferences,
    savePremiumPreferences,
    setViewMode,
    setSortField,
    setSortDirection,
    applyFilters,
  }), [
    opportunities,
    filteredOpportunities,
    filters,
    premiumPreferences,
    loading,
    error,
    viewMode,
    sortField,
    sortDirection,
    appliedFilters,
    loadOpportunities,
    loadFilters,
    loadPremiumPreferences,
    savePremiumPreferences,
    setViewMode,
    setSortField,
    setSortDirection,
    applyFilters,
  ]);

  return (
    <OpportunityContext.Provider value={value}>
      {children}
    </OpportunityContext.Provider>
  );
};
